import './styles/graphic.scss';
import { scaleSqrt } from 'd3-scale';
import { max } from 'd3-array';
import { select } from 'd3-selection';
import {
  forceSimulation, forceLink, forceManyBody, forceCenter, forceCollide, forceX,
} from 'd3-force';
import 'd3-transition';
import { classify } from './utils/helpers';

export default function (props, container, isMobile) {
  const {
    initialWidth,
    initialHeight,
    data,
  } = props;

  // ============================================
  // CONFIGURATION - CNN Style Guide colors
  // ============================================
  const nodeColorDefault = '#262626'; // CNN text color
  const backgroundColor = '#FEFEFE'; // CNN chart background

  // Link colors
  const linkColor = '#53A796'; // Green for all deals
  const linkColorHighlight = '#3d7a6e'; // Darker green for highlight
  const nodeHighlightColor = '#F18851'; // Orange for selected node

  // Node sizing
  const nodeRadius = isMobile ? 24 : 36;
  const nodeRadiusSelected = isMobile ? 36 : 48;

  // ============================================
  // DATA PROCESSING
  // ============================================

  function parseDealAmount(amountStr) {
    if (!amountStr || amountStr.includes('Not disclosed')) return null;
    const match = amountStr.match(/\$?([\d,.]+)\s*(billion|million)?/i);
    if (!match) return null;
    let value = parseFloat(match[1].replace(/,/g, ''));
    if (match[2] && match[2].toLowerCase() === 'million') {
      value /= 1000;
    }
    return value;
  }

  function cleanCompanyName(name) {
    if (!name) return null;
    const cleaned = name.trim();
    const fixes = {
      OpenaAI: 'OpenAI',
      OpneAI: 'OpenAI',
      Braodcom: 'Broadcom',
      'Nvidia ': 'Nvidia',
    };
    return fixes[cleaned] || cleaned;
  }

  function formatDate(dateInput) {
    if (!dateInput) return '';
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

    let date;

    if (typeof dateInput === 'number') {
      // Excel serial number - convert to JS Date
      // Excel dates are days since Dec 30, 1899
      // 25569 = days between Dec 30, 1899 and Jan 1, 1970 (Unix epoch)
      date = new Date((dateInput - 25569) * 86400 * 1000);
    } else if (typeof dateInput === 'string') {
      // Parse MM/DD/YYYY string
      const parts = dateInput.split('/');
      if (parts.length === 3) {
        const month = months[parseInt(parts[0], 10) - 1];
        const day = parseInt(parts[1], 10);
        const year = parts[2];
        return `${month} ${day}, ${year}`;
      }
      // Try parsing as Date string
      date = new Date(dateInput);
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return String(dateInput);
    }

    if (date && !Number.isNaN(date.getTime())) {
      return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
    }
    return String(dateInput);
  }

  // Build nodes and links
  const companies = new Set();
  const links = [];

  data.forEach((row, index) => {
    const partners = [
      cleanCompanyName(row['Deal Partner 1']),
      cleanCompanyName(row['Deal Partner 2']),
      cleanCompanyName(row['Deal Partner 3']),
      cleanCompanyName(row['Deal Partner 4']),
    ].filter(p => p);

    partners.forEach(p => companies.add(p));

    const amount = parseDealAmount(row['Deal $ Amount']);

    // Get deal type (monetary or non-monetary)
    const dealType = (row.deal_type || row.deal_type || 'monetary').toLowerCase().trim();
    const isMonetary = dealType !== 'non-monetary' && dealType !== 'nonmonetary';

    // Create links for each pair
    for (let i = 0; i < partners.length; i++) {
      for (let j = i + 1; j < partners.length; j++) {
        links.push({
          id: `link-${index}-${i}-${j}`,
          source: partners[i],
          target: partners[j],
          amount,
          amountStr: row['Deal $ Amount'],
          details: row.description,
          date: row['Announcement Date'],
          allPartners: partners,
          isMonetary,
          dealType,
        });
      }
    }
  });

  // Calculate link offsets for parallel links between same companies
  const linkPairs = {};
  links.forEach((link) => {
    // Create a consistent key regardless of source/target order
    const key = [link.source, link.target].sort().join('||');
    if (!linkPairs[key]) linkPairs[key] = [];
    linkPairs[key].push(link);
  });

  // Assign offset index to each link
  Object.values(linkPairs).forEach((pairLinks) => {
    const count = pairLinks.length;
    pairLinks.forEach((link, i) => {
      // Offset from -1 to 1, centered around 0
      link.offsetIndex = count === 1 ? 0 : (i - (count - 1) / 2) / ((count - 1) / 2 || 1);
      link.pairCount = count;
    });
  });

  // Create nodes array with deal counts
  const dealCounts = {};
  Array.from(companies).forEach((c) => { dealCounts[c] = 0; });

  links.forEach((link) => {
    const sourceId = link.source.id || link.source;
    const targetId = link.target.id || link.target;
    dealCounts[sourceId] = (dealCounts[sourceId] || 0) + 1;
    dealCounts[targetId] = (dealCounts[targetId] || 0) + 1;
  });

  // On desktop, use a narrower internal width for the force simulation
  const internalWidthForNodes = isMobile ? initialWidth : initialWidth * 0.7;

  // Nodes that should be fixed to the left on desktop
  const leftFixedNodes = { Cisco: 0.12, Oracle: 0.18 };

  const nodes = Array.from(companies).map((name) => {
    let fx;
    if (name === 'OpenAI') {
      fx = internalWidthForNodes / 2;
    } else if (!isMobile && leftFixedNodes[name] !== undefined) {
      fx = internalWidthForNodes * leftFixedNodes[name];
    }
    return {
      id: name,
      name,
      dealCount: dealCounts[name] || 0,
      fx,
      fy: name === 'OpenAI' ? initialHeight / 2 : undefined,
    };
  });

  // Get top companies by deal count (for showing labels on mobile)
  const topCompanyCount = 6;
  const topCompanies = new Set(
    [...nodes]
      .sort((a, b) => b.dealCount - a.dealCount)
      .slice(0, topCompanyCount)
      .map(n => n.id),
  );

  // Scale for link thickness
  const maxAmount = max(links, d => d.amount) || 100;
  const linkWidthScale = scaleSqrt()
    .domain([0, maxAmount])
    .range([2, isMobile ? 10 : 16]);

  // ============================================
  // RENDERING
  // ============================================

  // On desktop, use a narrower internal width to make the network fill more of the space
  const internalWidth = isMobile ? initialWidth : initialWidth * 0.7;
  const internalHeight = initialHeight;

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', initialHeight)
    .attr('viewBox', `0 0 ${internalWidth} ${internalHeight}`)
    .attr('preserveAspectRatio', 'xMidYMid meet')
    .attr('class', 'network-diagram');

  // Background
  svg.append('rect')
    .attr('width', internalWidth)
    .attr('height', internalHeight)
    .attr('fill', backgroundColor);

  const g = svg.append('g').attr('class', 'main-group');

  // Create force simulation
  const simulation = forceSimulation(nodes)
    .force('link', forceLink(links)
      .id(d => d.id)
      .distance(isMobile ? 90 : 120))
    .force('charge', forceManyBody().strength(isMobile ? -400 : -600))
    .force('center', forceCenter(internalWidth / 2, internalHeight / 2))
    .force('collide', forceCollide().radius(nodeRadius + 15));

  // Draw links as curved paths
  const linksGroup = g.append('g').attr('class', 'links');

  // Function to calculate curved path with offset
  function getLinkPath(d) {
    const sourceX = d.source.x || 0;
    const sourceY = d.source.y || 0;
    const targetX = d.target.x || 0;
    const targetY = d.target.y || 0;

    // Calculate direction vector
    const dx = targetX - sourceX;
    const dy = targetY - sourceY;
    const dist = Math.sqrt(dx * dx + dy * dy);

    // Unit vector along the line
    const ux = dx / (dist || 1);
    const uy = dy / (dist || 1);

    // Perpendicular unit vector
    const px = -dy / (dist || 1);
    const py = dx / (dist || 1);

    // Offset amount based on link's offset index
    const offsetAmount = (d.offsetIndex || 0) * (isMobile ? 25 : 40);

    // Additional offset for undisclosed deals to separate them from disclosed deals
    const undisclosedOffset = d.amount ? 0 : (isMobile ? -50 : -80);

    // Control point at midpoint, offset perpendicular to the line
    const midX = (sourceX + targetX) / 2 + px * offsetAmount;
    const midY = (sourceY + targetY) / 2 + py * offsetAmount + undisclosedOffset;

    return `M ${sourceX} ${sourceY} Q ${midX} ${midY} ${targetX} ${targetY}`;
  }

  const linkElements = linksGroup.selectAll('.link')
    .data(links)
    .enter()
    .append('path')
    .attr('class', d => `link ${classify(d.source.id || d.source)} ${classify(d.target.id || d.target)}${d.amount ? '' : ' undisclosed'}`)
    .attr('fill', 'none')
    .attr('stroke', linkColor)
    .attr('stroke-width', d => (d.amount ? linkWidthScale(d.amount) : 3))
    .attr('stroke-opacity', 0.6)
    .attr('stroke-linecap', 'round')
    .attr('stroke-dasharray', d => (d.amount ? 'none' : '6,4'));

  // Draw nodes
  const nodesGroup = g.append('g').attr('class', 'nodes');

  const nodeElements = nodesGroup.selectAll('.node')
    .data(nodes)
    .enter()
    .append('g')
    .attr('class', d => `node node-${classify(d.id)}`)
    .style('cursor', 'pointer');

  // Node circles
  nodeElements.append('circle')
    .attr('r', nodeRadius)
    .attr('fill', nodeColorDefault)
    .attr('stroke', '#fff')
    .attr('stroke-width', 3);

  // Node labels
  const labelFontSize = 12;
  let labelElements;

  if (isMobile) {
    // Mobile: labels in separate group on top, show top companies by default
    const labelsGroup = g.append('g').attr('class', 'labels-layer');

    labelElements = labelsGroup.selectAll('.node-label')
      .data(nodes)
      .enter()
      .append('text')
      .attr('class', d => `node-label mobile-label${topCompanies.has(d.id) ? ' top-company' : ''}`)
      .attr('data-id', d => d.id)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'hanging')
      .attr('fill', '#262626')
      .attr('font-size', labelFontSize)
      .attr('font-weight', 600)
      .attr('opacity', d => (topCompanies.has(d.id) ? 1 : 0))
      .text(d => d.name);
  } else {
    // Desktop: labels inside circles, always visible
    nodeElements.append('text')
      .attr('class', 'node-label desktop-label')
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .attr('fill', '#fff')
      .attr('font-size', labelFontSize)
      .attr('font-weight', 600)
      .attr('opacity', 1)
      .text(d => d.name)
      .each(function (d) {
        // Wrap long names for desktop
        const text = select(this);
        const words = d.name.split(/\s+/);
        if (words.length > 1) {
          text.text('');
          words.forEach((word, i) => {
            text.append('tspan')
              .attr('x', 0)
              .attr('dy', i === 0 ? `-${(words.length - 1) * 0.5}em` : '1.1em')
              .text(word);
          });
        }
      });
  }

  // ============================================
  // TOOLTIP
  // ============================================

  const tooltip = container.append('div')
    .attr('class', 'arc-tooltip')
    .style('opacity', 0);

  // ============================================
  // INTERACTIVITY
  // ============================================

  let selectedNode = null;

  function highlightNode(nodeId) {
    selectedNode = nodeId;
    hideOverlay();

    // Show reset instructions
    resetInstructions
      .transition()
      .duration(300)
      .style('opacity', 1);

    // Find connected nodes
    const connectedNodes = new Set();
    connectedNodes.add(nodeId);
    links.forEach((link) => {
      const sourceId = link.source.id || link.source;
      const targetId = link.target.id || link.target;
      if (sourceId === nodeId) connectedNodes.add(targetId);
      if (targetId === nodeId) connectedNodes.add(sourceId);
    });

    // Highlight links
    linkElements
      .transition()
      .duration(300)
      .attr('stroke', (d) => {
        const sourceId = d.source.id || d.source;
        const targetId = d.target.id || d.target;
        const isConnected = sourceId === nodeId || targetId === nodeId;
        if (isConnected) {
          return linkColorHighlight;
        }
        return linkColor;
      })
      .attr('stroke-opacity', (d) => {
        const sourceId = d.source.id || d.source;
        const targetId = d.target.id || d.target;
        return (sourceId === nodeId || targetId === nodeId) ? 0.9 : 0.15;
      });

    // Highlight nodes
    nodeElements.each(function (d) {
      const isSelected = d.id === nodeId;
      const isConnected = connectedNodes.has(d.id);
      const node = select(this);

      // Bring selected/connected nodes to front on mobile
      if (isMobile && (isSelected || isConnected)) {
        this.parentNode.appendChild(this);
      }

      node.select('circle')
        .transition()
        .duration(300)
        .attr('r', isSelected ? nodeRadiusSelected : nodeRadius)
        .attr('fill', isSelected ? nodeHighlightColor : (isConnected ? nodeColorDefault : '#bfbfbf'));

      if (!isMobile) {
        // Desktop: labels always visible, always white
        node.select('text')
          .transition()
          .duration(300)
          .attr('fill', '#fff');
      }
    });

    // Mobile: update labels in separate layer
    if (isMobile && labelElements) {
      labelElements
        .transition()
        .duration(300)
        .attr('opacity', (d) => {
          const isSelected = d.id === nodeId;
          const isConnected = connectedNodes.has(d.id);
          return isSelected || isConnected ? 1 : 0;
        })
        .attr('y', (d) => {
          const isSelected = d.id === nodeId;
          return d.y + (isSelected ? nodeRadiusSelected + 6 : nodeRadius + 6);
        });
    }
  }

  function resetHighlight() {
    selectedNode = null;

    // Hide reset instructions
    resetInstructions
      .transition()
      .duration(300)
      .style('opacity', 0);

    linkElements
      .transition()
      .duration(300)
      .attr('stroke', d => linkColor)
      .attr('stroke-opacity', 0.6);

    nodeElements.select('circle')
      .transition()
      .duration(300)
      .attr('r', nodeRadius)
      .attr('fill', nodeColorDefault);

    if (isMobile && labelElements) {
      // Mobile: restore top company labels, hide others
      labelElements
        .transition()
        .duration(300)
        .attr('opacity', d => (topCompanies.has(d.id) ? 1 : 0))
        .attr('y', d => d.y + nodeRadius + 6);
    } else if (!isMobile) {
      // Desktop: restore label colors
      nodeElements.select('text')
        .transition()
        .duration(300)
        .attr('fill', '#fff');
    }
  }

  // Click handlers for nodes
  nodeElements
    .on('click', (event, d) => {
      event.stopPropagation();
      if (selectedNode === d.id) {
        resetHighlight();
      } else {
        highlightNode(d.id);
      }
    });

  // Mobile-only hover handlers
  if (isMobile && labelElements) {
    nodeElements
      .on('mouseenter', (event, d) => {
        if (!selectedNode) {
          // Show label for this node
          labelElements.filter(ld => ld.id === d.id)
            .transition()
            .duration(150)
            .attr('opacity', 1);
        }
      })
      .on('mouseleave', (event, d) => {
        if (!selectedNode) {
          // Keep top company labels visible
          labelElements.filter(ld => ld.id === d.id)
            .transition()
            .duration(150)
            .attr('opacity', topCompanies.has(d.id) ? 1 : 0);
        }
      });
  }

  // Hover handlers for links
  linkElements
    .on('mouseenter', function (event, d) {
      if (!selectedNode) {
        select(this)
          .attr('stroke', linkColorHighlight)
          .attr('stroke-opacity', 0.9);
      }

      tooltip
        .style('opacity', 1)
        .html(`
          <div class="tooltip-partners">${d.allPartners.join(' + ')}</div>
          <div class="tooltip-amount">${d.amountStr}</div>
          ${d.details ? `<div class="tooltip-details">${d.details}</div>` : ''}
          <div class="tooltip-date">${formatDate(d.date)}</div>
        `);

      // Position tooltip - center on mobile, follow cursor on desktop
      if (isMobile) {
        tooltip
          .style('left', '50%')
          .style('transform', 'translateX(-50%)')
          .style('top', '120px');
      } else {
        tooltip
          .style('left', `${event.pageX + 10}px`)
          .style('transform', 'none')
          .style('top', `${event.pageY + 15}px`);
      }
    })
    .on('mouseleave', function (event, d) {
      if (!selectedNode) {
        select(this)
          .attr('stroke', linkColor)
          .attr('stroke-opacity', 0.6);
      } else {
        const sourceId = d.source.id || d.source;
        const targetId = d.target.id || d.target;
        const isConnected = sourceId === selectedNode || targetId === selectedNode;
        if (!isConnected) {
          select(this)
            .attr('stroke', linkColor)
            .attr('stroke-opacity', 0.15);
        }
      }
      tooltip
        .style('opacity', 0)
        .style('transform', 'none');
    });

  // Click on background to reset
  svg.on('click', () => {
    if (selectedNode) {
      resetHighlight();
    }
  });

  // ============================================
  // SIMULATION TICK
  // ============================================

  simulation.on('tick', () => {
    // Keep nodes within bounds (extra bottom padding on mobile for labels)
    const bottomPadding = isMobile ? nodeRadius + 15 : nodeRadius;
    const sidePadding = nodeRadius;
    nodes.forEach((d) => {
      // Don't constrain fixed nodes (OpenAI)
      if (d.fx === undefined) {
        d.x = Math.max(sidePadding, Math.min(internalWidth - sidePadding, d.x));
      }
      if (d.fy === undefined) {
        d.y = Math.max(nodeRadius, Math.min(internalHeight - bottomPadding, d.y));
      }
    });

    linkElements
      .attr('d', getLinkPath);

    nodeElements
      .attr('transform', d => `translate(${d.x}, ${d.y})`);

    // Update mobile label positions
    if (isMobile && labelElements) {
      labelElements
        .attr('x', d => d.x)
        .attr('y', d => d.y + nodeRadius + 6);
    }
  });

  // ============================================
  // LEGEND / INSTRUCTIONS OVERLAY
  // ============================================

  // Create overlay that appears on top of the graphic
  const overlay = container.append('div')
    .attr('class', 'instructions-overlay');

  overlay.append('div')
    .attr('class', 'instructions-box')
    .html('<span class="instructions-text">Click a company to highlight its deals</span>');

  // Function to hide overlay
  function hideOverlay() {
    overlay
      .transition()
      .duration(300)
      .style('opacity', 0)
      .on('end', () => {
        overlay.style('display', 'none');
      });
  }

  // Click overlay to dismiss
  overlay.on('click', hideOverlay);

  // Reset instructions (shown when a company is selected)
  const resetInstructions = container.append('div')
    .attr('class', 'reset-instructions')
    .style('opacity', 0)
    .text('Click anywhere to reset');

  // HTML Legend (above the graphic)
  const legend = container.insert('div', 'svg')
    .attr('class', 'legend-container');

  // Legend title
  legend.append('div')
    .attr('class', 'legend-title')
    .text('Estimated deal value');

  const legendItems = legend.append('div')
    .attr('class', 'legend-items');

  // Undisclosed item
  const undisclosedItem = legendItems.append('div')
    .attr('class', 'legend-item undisclosed');

  undisclosedItem.append('div')
    .attr('class', 'legend-line dashed')
    .style('height', '3px')
    .style('background', 'repeating-linear-gradient(90deg, #A6A6A6 0, #A6A6A6 6px, transparent 6px, transparent 10px)');

  undisclosedItem.append('span')
    .attr('class', 'legend-label')
    .text('Amount not disclosed');

  // Deal size legend items
  const sampleAmounts = [10, 100, 300];
  sampleAmounts.forEach((amount) => {
    const width = linkWidthScale(amount);
    const item = legendItems.append('div')
      .attr('class', 'legend-item');

    item.append('div')
      .attr('class', 'legend-line')
      .style('height', `${width}px`)
      .style('background', linkColor);

    item.append('span')
      .attr('class', 'legend-label')
      .text(`$${amount}B`);
  });

  // Instructions below the legend
  const legendInstructions = legend.append('div')
    .attr('class', 'legend-instructions');

  legendInstructions.append('img')
    .attr('src', './click-symbol.png')
    .attr('class', 'legend-instructions-icon')
    .attr('alt', 'Click');

  legendInstructions.append('span')
    .attr('class', 'legend-instructions-text')
    .text('Click a company to highlight its deals, and hover over a line to see the details of each deal');
}
